function defaultBaseUrl(): string {
  // Servido desde un dominio real: la API vive en el mismo origen detrás del proxy,
  // así que una URL relativa evita romperse si cambia el esquema o el puerto.
  const host = typeof window === "undefined" ? "" : window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" ? "http://localhost:5001" : "";
}

export const API_BASE_URL =
  ((process.env.REACT_APP_API_BASE_URL as string | undefined) ?? "").trim().replace(/\/+$/, "") || defaultBaseUrl();

export type ApiError = { status: number; body: any };

export type Permissions = Record<string, string[]>;

export type LoginResponse = {
  access_token?: string;
  require_code?: boolean;
  method?: "email" | "totp";
  challenge_id?: string;
  challenge_token?: string;
  email_hint?: string;
  expires_in?: number;
  resend_in?: number;
};

export type StaffUser = {
  id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  email: string | null;
  is_active: boolean;
  is_admin: boolean;
  role: { id: number; name: string; is_admin: boolean } | null;
  last_login_at: string | null;
  created_at: string | null;
  totp_enabled?: boolean;
};

export type Me = StaffUser & { permissions: Permissions; smtp_configured: boolean; totp_enabled?: boolean };

export type RoleItem = {
  id: number;
  name: string;
  description: string | null;
  is_admin: boolean;
  permissions: Permissions;
  users_count: number;
};

export type PermissionsCatalog = {
  modules: { id: string; label: string; actions: string[] }[];
  actions: { id: string; label: string }[];
};

let _pendingRequests = 0;

function emitLoading() {
  try {
    window.dispatchEvent(new CustomEvent("sc:loading", { detail: { pending: _pendingRequests } }));
  } catch {
    // ignore
  }
}

function loadingStart() {
  _pendingRequests += 1;
  emitLoading();
}

function loadingEnd() {
  _pendingRequests = Math.max(0, _pendingRequests - 1);
  emitLoading();
}

function getToken(): string | null {
  return localStorage.getItem("sc_token");
}

export function setToken(token: string | null) {
  if (!token) localStorage.removeItem("sc_token");
  else localStorage.setItem("sc_token", token);
}

async function request(path: string, init: RequestInit = {}) {
  loadingStart();
  const headers = new Headers(init.headers || {});
  headers.set("Content-Type", "application/json");

  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  try {
    let res: Response;
    try {
      res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
    } catch (e: any) {
      const err: ApiError = { status: 0, body: { error: "network_error", message: String(e?.message ?? e) } };
      throw err;
    }

    const text = await res.text();
    const body = text ? safeJson(text) : null;

    if (!res.ok) {
      // Sesión expirada / token inválido: limpiar y notificar al resto de la app
      if (res.status === 401) {
        setToken(null);
        try {
          window.dispatchEvent(new CustomEvent("sc:unauthorized", { detail: { path } }));
        } catch {
          // ignore
        }
      }
      const err: ApiError = { status: res.status, body };
      throw err;
    }
    return body;
  } finally {
    loadingEnd();
  }
}

async function requestBlob(path: string): Promise<Blob> {
  loadingStart();
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, { headers });
    if (!res.ok) {
      if (res.status === 401) {
        setToken(null);
        window.dispatchEvent(new CustomEvent("sc:unauthorized", { detail: { path } }));
      }
      let body: any = null;
      try {
        body = await res.json();
      } catch {
        body = { error: "download_failed", message: "No se pudo descargar el archivo." };
      }
      throw { status: res.status, body } as ApiError;
    }
    return res.blob();
  } finally {
    loadingEnd();
  }
}

async function pdfObjectUrl(path: string): Promise<string> {
  const blob = await requestBlob(path);
  return URL.createObjectURL(blob);
}

async function requestForm(path: string, init: RequestInit = {}) {
  loadingStart();
  const headers = new Headers(init.headers || {});
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  try {
    let res: Response;
    try {
      res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
    } catch (e: any) {
      const err: ApiError = { status: 0, body: { error: "network_error", message: String(e?.message ?? e) } };
      throw err;
    }
    const text = await res.text();
    const body = text ? safeJson(text) : null;
    if (!res.ok) {
      if (res.status === 401) {
        setToken(null);
        try {
          window.dispatchEvent(new CustomEvent("sc:unauthorized", { detail: { path } }));
        } catch {
          /* ignore */
        }
      }
      const err: ApiError = { status: res.status, body };
      throw err;
    }
    return body;
  } finally {
    loadingEnd();
  }
}

function safeJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  // dashboard
  getDashboardSummary() {
    return request("/api/dashboard/summary");
  },

  // auth
  bootstrap(payload: {
    username: string;
    first_name: string;
    last_name: string;
    password: string;
    email: string;
    bootstrapToken: string;
    recaptchaToken: string;
  }) {
    const { bootstrapToken, recaptchaToken, ...rest } = payload;
    return request("/api/auth/bootstrap", {
      method: "POST",
      headers: { "X-Bootstrap-Token": bootstrapToken },
      body: JSON.stringify({ ...rest, recaptcha_token: recaptchaToken }),
    });
  },
  login(identifier: string, password: string, recaptchaToken: string): Promise<LoginResponse> {
    return request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ identifier, password, recaptcha_token: recaptchaToken }),
    });
  },
  verifyLoginCode(challenge_id: string, challenge_token: string, code: string): Promise<LoginResponse> {
    return request("/api/auth/login/verify", {
      method: "POST",
      body: JSON.stringify({ challenge_id, challenge_token, code }),
    });
  },
  resendLoginCode(challenge_id: string, challenge_token: string): Promise<{ ok: boolean; resend_in: number; method?: string }> {
    return request("/api/auth/login/resend", {
      method: "POST",
      body: JSON.stringify({ challenge_id, challenge_token }),
    });
  },
  logout() {
    return request("/api/auth/logout", { method: "POST", body: "{}" });
  },
  me(): Promise<Me> {
    return request("/api/auth/me");
  },
  updateMe(payload: {
    current_password: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    new_password?: string;
  }): Promise<Me & { access_token?: string }> {
    return request("/api/auth/me", { method: "PUT", body: JSON.stringify(payload) });
  },
  startTotp(current_password: string): Promise<{ secret: string; otpauth_url: string; qr_svg: string }> {
    return request("/api/auth/me/totp/start", { method: "POST", body: JSON.stringify({ current_password }) });
  },
  confirmTotp(code: string): Promise<Me> {
    return request("/api/auth/me/totp/confirm", { method: "POST", body: JSON.stringify({ code }) });
  },
  disableTotp(current_password: string): Promise<Me> {
    return request("/api/auth/me/totp/disable", { method: "POST", body: JSON.stringify({ current_password }) });
  },

  // usuarios y roles
  listUsers(): Promise<StaffUser[]> {
    return request("/api/users");
  },
  createUser(payload: {
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    role_id: number;
    is_active?: boolean;
  }) {
    return request("/api/users", { method: "POST", body: JSON.stringify(payload) });
  },
  updateUser(
    id: number,
    payload: Partial<{
      username: string;
      first_name: string;
      last_name: string;
      email: string;
      password: string;
      role_id: number;
      is_active: boolean;
    }>,
  ) {
    return request(`/api/users/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteUser(id: number) {
    return request(`/api/users/${id}`, { method: "DELETE" });
  },
  getPermissionsCatalog(): Promise<PermissionsCatalog> {
    return request("/api/roles/catalog");
  },
  listRoles(): Promise<RoleItem[]> {
    return request("/api/roles");
  },
  createRole(payload: { name: string; description?: string; permissions: Permissions }) {
    return request("/api/roles", { method: "POST", body: JSON.stringify(payload) });
  },
  updateRole(id: number, payload: Partial<{ name: string; description: string; permissions: Permissions }>) {
    return request(`/api/roles/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteRole(id: number) {
    return request(`/api/roles/${id}`, { method: "DELETE" });
  },

  // clients
  listClients(opts?: {
    q?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_dir?: "asc" | "desc";
  }) {
    if (!opts) return request("/api/clients");
    const params = new URLSearchParams();
    if (opts.q) params.set("q", opts.q);
    if (typeof opts.limit === "number") params.set("limit", String(opts.limit));
    if (typeof opts.offset === "number") params.set("offset", String(opts.offset));
    if (opts.sort_by) params.set("sort_by", opts.sort_by);
    if (opts.sort_dir) params.set("sort_dir", opts.sort_dir);
    const qs = params.toString();
    return request(`/api/clients${qs ? `?${qs}` : ""}`);
  },
  getClient(id: number) {
    return request(`/api/clients/${id}`);
  },
  suspendClientServices(id: number, cut_profile?: string) {
    return request(`/api/clients/${id}/suspend_services`, {
      method: "POST",
      body: JSON.stringify({ cut_profile }),
    });
  },
  createClient(payload: any) {
    return request("/api/clients", { method: "POST", body: JSON.stringify(payload) });
  },
  updateClient(id: number, payload: any) {
    return request(`/api/clients/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  getClientPortal(id: number) {
    return request(`/api/clients/${id}/portal`);
  },
  upsertClientPortal(id: number, payload: { enabled?: boolean; password?: string }) {
    return request(`/api/clients/${id}/portal`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteClient(id: number) {
    return request(`/api/clients/${id}`, { method: "DELETE" });
  },

  // connections
  createConnection(payload: any) {
    return request("/api/connections", { method: "POST", body: JSON.stringify(payload) });
  },
  updateConnection(id: number, payload: any) {
    return request(`/api/connections/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  getConnectionMtStatus(id: number) {
    return request(`/api/connections/${id}/mt_status`);
  },
  cutConnection(id: number, cut_profile?: string) {
    return request(`/api/connections/${id}/cut`, {
      method: "POST",
      body: JSON.stringify({ cut_profile }),
    });
  },
  restoreConnection(id: number) {
    return request(`/api/connections/${id}/restore`, { method: "POST", body: JSON.stringify({}) });
  },
  deleteConnection(id: number) {
    return request(`/api/connections/${id}`, { method: "DELETE" });
  },

  // invoices
  listInvoices(client_id?: number) {
    const qs = client_id ? `?client_id=${client_id}` : "";
    return request(`/api/invoices${qs}`);
  },
  listInvoicesPage(params: { client_id?: number; invoice_type?: string; status?: string; limit: number; offset: number }) {
    const qs = new URLSearchParams();
    if (params.client_id) qs.set("client_id", String(params.client_id));
    if (params.invoice_type) qs.set("invoice_type", params.invoice_type);
    if (params.status) qs.set("status", params.status);
    qs.set("limit", String(params.limit));
    qs.set("offset", String(params.offset));
    return request(`/api/invoices?${qs.toString()}`);
  },
  createInvoice(payload: any) {
    return request("/api/invoices", { method: "POST", body: JSON.stringify(payload) });
  },
  issueInvoice(id: number) {
    return request(`/api/invoices/${id}/issue`, { method: "POST", body: JSON.stringify({}) });
  },
  deleteInvoice(id: number) {
    return request(`/api/invoices/${id}`, { method: "DELETE" });
  },

  // payments
  listPayments(
    client_id?: number,
    opts?: { from?: string; to?: string; day?: string; month?: string; year?: string }
  ) {
    const params = new URLSearchParams();
    if (client_id) params.set("client_id", String(client_id));
    if (opts?.from) params.set("from", opts.from);
    if (opts?.to) params.set("to", opts.to);
    if (opts?.day) params.set("day", opts.day);
    if (opts?.month) params.set("month", opts.month);
    if (opts?.year) params.set("year", opts.year);
    const qs = params.toString();
    return request(`/api/payments${qs ? `?${qs}` : ""}`);
  },
  createPayment(payload: any) {
    return request("/api/payments", { method: "POST", body: JSON.stringify(payload) });
  },

  // settings
  getIssuer() {
    return request("/api/settings/issuer");
  },
  putIssuer(payload: any) {
    return request("/api/settings/issuer", { method: "PUT", body: JSON.stringify(payload) });
  },
  getSettings(prefix?: string) {
    const qs = prefix ? `?prefix=${encodeURIComponent(prefix)}` : "";
    return request(`/api/settings/kv${qs}`);
  },
  putSettings(values: Record<string, string>) {
    return request("/api/settings/kv", { method: "PUT", body: JSON.stringify({ values }) });
  },
  uploadArcaCerts(files: { cert?: File | null; key?: File | null }) {
    const fd = new FormData();
    if (files.cert) fd.append("cert", files.cert);
    if (files.key) fd.append("key", files.key);
    return requestForm("/api/settings/arca-certs", { method: "POST", body: fd });
  },
  getMigrationStatus() {
    return request("/api/settings/migration/status");
  },
  getSafetyStatus() {
    return request("/api/settings/safety");
  },
  checkMp() {
    return request("/api/settings/mp-check");
  },
  uploadMigrationBackup(file: File) {
    const fd = new FormData();
    fd.append("file", file);
    return requestForm("/api/settings/migration/upload", { method: "POST", body: fd });
  },
  previewMigration() {
    return request("/api/settings/migration/preview", { method: "POST", body: "{}" });
  },
  applyMigration() {
    return request("/api/settings/migration/apply", { method: "POST", body: "{}" });
  },
  applyMigrationEmails(overwrite = false) {
    return request("/api/settings/migration/apply-emails", {
      method: "POST",
      body: JSON.stringify({ overwrite }),
    });
  },

  // billing
  generateBilling(payload: any) {
    return request("/api/billing/generate", { method: "POST", body: JSON.stringify(payload) });
  },
  updateServices(payload?: any) {
    return request("/api/billing/update-services", { method: "POST", body: JSON.stringify(payload || {}) });
  },

  // complaints
  listComplaints(client_id?: number) {
    const qs = client_id ? `?client_id=${client_id}` : "";
    return request(`/api/complaints${qs}`);
  },
  createComplaint(payload: any) {
    return request("/api/complaints", { method: "POST", body: JSON.stringify(payload) });
  },
  updateComplaint(id: number, payload: any) {
    return request(`/api/complaints/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },

  // network (pppoe servers)
  listServers() {
    return request("/api/network/servers");
  },
  getServer(id: number) {
    return request(`/api/network/servers/${id}`);
  },
  createServer(payload: any) {
    return request("/api/network/servers", { method: "POST", body: JSON.stringify(payload) });
  },
  updateServer(id: number, payload: any) {
    return request(`/api/network/servers/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteServer(id: number) {
    return request(`/api/network/servers/${id}`, { method: "DELETE" });
  },
  testServerConnection(serverId: number, overrides?: { host?: string; port?: number; username?: string; password?: string; use_ssl?: boolean }) {
    return request(`/api/network/servers/${serverId}/test`, {
      method: "POST",
      body: JSON.stringify(overrides || {}),
    });
  },
  testConnectionInline(payload: { host: string; port: number; username: string; password: string; use_ssl?: boolean }) {
    return request("/api/network/servers/test", { method: "POST", body: JSON.stringify(payload) });
  },
  listServerJobs(server_id: number) {
    return request(`/api/network/servers/${server_id}/jobs`);
  },
  getServerPool(server_id: number, max_listed = 1024) {
    const qs = `?max_listed=${encodeURIComponent(String(max_listed))}`;
    return request(`/api/network/servers/${server_id}/pool${qs}`);
  },
  retryJob(job_id: number) {
    return request(`/api/jobs/${job_id}/retry`, { method: "POST", body: JSON.stringify({}) });
  },
  cancelJob(job_id: number) {
    return request(`/api/jobs/${job_id}/cancel`, { method: "POST", body: JSON.stringify({}) });
  },
  recoverStuckJobs(server_id?: number) {
    const q = server_id != null ? `?server_id=${server_id}` : "";
    return request(`/api/jobs/recover-stuck${q}`, { method: "POST", body: JSON.stringify({}) });
  },
  listJobs(params?: { status?: string; job_type?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.job_type) qs.set("job_type", params.job_type);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.offset) qs.set("offset", String(params.offset));
    const q = qs.toString();
    return request(`/api/jobs${q ? `?${q}` : ""}`);
  },
  getJobTypes() {
    return request("/api/jobs/types");
  },

  // plans
  listPlans(activeOnly = false) {
    const qs = activeOnly ? "?active_only=1" : "";
    return request(`/api/plans${qs}`);
  },
  createPlan(payload: any) {
    return request("/api/plans", { method: "POST", body: JSON.stringify(payload) });
  },
  updatePlan(id: number, payload: any) {
    return request(`/api/plans/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deletePlan(id: number) {
    return request(`/api/plans/${id}`, { method: "DELETE" });
  },
  getAfipStatus() {
    return request("/api/invoices/afip/status");
  },

  // logs
  getLogs(opts?: {
    module?: string;
    level?: string;
    q?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (opts?.module) params.set("module", opts.module);
    if (opts?.level) params.set("level", opts.level);
    if (opts?.q) params.set("q", opts.q);
    if (opts?.from) params.set("from", opts.from);
    if (opts?.to) params.set("to", opts.to);
    if (typeof opts?.limit === "number") params.set("limit", String(opts.limit));
    if (typeof opts?.offset === "number") params.set("offset", String(opts.offset));
    const qs = params.toString();
    return request(`/api/logs${qs ? `?${qs}` : ""}`)
  },
  getLogModules() {
    return request("/api/logs/modules");
  },
  getUserActivity(opts?: {
    user_id?: number | null;
    module?: string | null;
    action?: string | null;
    q?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    Object.entries(opts || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    });
    const qs = params.toString();
    return request(`/api/logs/activity${qs ? `?${qs}` : ""}`);
  },
  getUserActivityMeta(): Promise<{
    users: { id: number; username: string }[];
    modules: { id: string; label: string }[];
    actions: { id: string; label: string }[];
  }> {
    return request("/api/logs/activity/meta");
  },
  getLoggingConfig() {
    return request("/api/logs/config");
  },
  updateLoggingConfig(payload: { enabled?: boolean; modules?: Record<string, boolean> }) {
    return request("/api/logs/config", { method: "PUT", body: JSON.stringify(payload) });
  },

  // invoice PDF & email
  getInvoicePdfUrl(id: number) {
    return pdfObjectUrl(`/api/invoices/${id}/pdf`);
  },
  sendInvoiceEmail(id: number, to?: string) {
    return request(`/api/invoices/${id}/send_email`, {
      method: "POST",
      body: JSON.stringify(to ? { to } : {}),
    });
  },

  // billing status
  getBillingStatus() {
    return request("/api/billing/status")
  },

  // installations (órdenes de instalación / reservas NAP)
  listInstallations(opts?: { status?: string; client_id?: number; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (opts?.status) params.set("status", opts.status);
    if (opts?.client_id) params.set("client_id", String(opts.client_id));
    if (typeof opts?.limit === "number") params.set("limit", String(opts.limit));
    if (typeof opts?.offset === "number") params.set("offset", String(opts.offset));
    const qs = params.toString();
    return request(`/api/installations${qs ? `?${qs}` : ""}`);
  },
  getInstallationsSummary() {
    return request("/api/installations/summary");
  },
  getInstallation(id: number) {
    return request(`/api/installations/${id}`);
  },
  createInstallation(payload: { client_id: number; connection_id?: number; location_url?: string; lat?: number; lng?: number; notes?: string }) {
    return request("/api/installations", { method: "POST", body: JSON.stringify(payload) });
  },
  previewCoverage(payload: { location_url?: string; lat?: number; lng?: number }) {
    return request("/api/installations/preview", { method: "POST", body: JSON.stringify(payload) });
  },
  retryInstallationCheck(id: number) {
    return request(`/api/installations/${id}/retry-check`, { method: "POST", body: JSON.stringify({}) });
  },
  confirmInstallation(id: number) {
    return request(`/api/installations/${id}/confirm`, { method: "POST", body: JSON.stringify({}) });
  },
  cancelInstallation(id: number) {
    return request(`/api/installations/${id}/cancel`, { method: "POST", body: JSON.stringify({}) });
  },
  updateInstallation(id: number, payload: { technician?: string; notes?: string }) {
    return request(`/api/installations/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  getInstallationPdfUrl(id: number) {
    return pdfObjectUrl(`/api/installations/${id}/pdf`);
  },
};

